import { useState, useEffect, useRef } from 'react';
import TelegramWebApp from '../telegram-init';
import WebApp from '@twa-dev/sdk';
import { paymentService } from '../services/payment.service';
import { PRICES, STARS_AMOUNT, type Period } from '../constants/pricing';

type PaymentMethod = 'stars' | 'crypto';

interface PaymentEvent {
  event: 'payment_success' | 'payment_failed';
  data?: {
    message?: string;
  };
}

interface UsePaymentOptions {
  period: Period;
  paymentMethod: PaymentMethod;
  onSuccess?: () => void;
}

export const usePayment = ({ period, paymentMethod, onSuccess }: UsePaymentOptions) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const eventListenerRef = useRef<((event: MessageEvent) => void) | null>(null);

  useEffect(() => {
    if (eventListenerRef.current) return;
    
    console.log('[Payment] Component mounted');
    
    const handleEvent = (event: MessageEvent) => {
      console.log('[Payment] Received event:', event.data);
      try {
        if (typeof event.data === 'string') {
          const data = JSON.parse(event.data) as PaymentEvent;
          console.log('[Payment] Parsed event data:', data);
          
          if (data.event === 'payment_success') {
            console.log('[Payment] Payment successful');
            TelegramWebApp.HapticFeedback.notificationOccurred('success');
            TelegramWebApp.showPopup({
              title: 'Payment Successful',
              message: 'Your subscription has been successfully activated!',
              buttons: [{ type: 'default', text: 'OK' }]
            });
            onSuccess?.();
          } else if (data.event === 'payment_failed') {
            console.log('[Payment] Payment failed:', data.data?.message);
            TelegramWebApp.HapticFeedback.notificationOccurred('error');
            TelegramWebApp.showPopup({
              title: 'Payment Error',
              message: data.data?.message || 'Failed to process payment. Please try again.',
              buttons: [{ type: 'default', text: 'OK' }]
            });
          }
        }
      } catch (error) {
        console.error('[Payment] Error handling bot event:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    eventListenerRef.current = handleEvent;
    window.addEventListener('message', handleEvent);
    
    return () => {
      if (eventListenerRef.current) {
        console.log('[Payment] Component unmounted');
        window.removeEventListener('message', eventListenerRef.current);
        eventListenerRef.current = null;
      }
    };
  }, [onSuccess]);

  const handleStarsPayment = async () => {
    try {
      console.log('[Payment] Starting stars payment...');
      
      console.log('[Payment] Creating payment record:', { amount: STARS_AMOUNT[period], period });
      const { invoiceLink } = await paymentService.createStarsPayment(STARS_AMOUNT[period], period);
      console.log('[Payment] Payment record created:', invoiceLink);

      if (!invoiceLink) {
        throw new Error('Invoice link is empty');
      }

      try {
        WebApp.openInvoice(invoiceLink, (status) => {
          console.log('[Payment] Invoice status:', status);
          
          if (status === 'paid') {
            TelegramWebApp.HapticFeedback.notificationOccurred('success');
            TelegramWebApp.showPopup({
              title: 'Payment Successful',
              message: 'Your subscription has been successfully activated!',
              buttons: [{ type: 'default', text: 'OK' }]
            });
            onSuccess?.();
          } else if (status === 'failed') {
            TelegramWebApp.HapticFeedback.notificationOccurred('error');
            
            TelegramWebApp.showPopup({
              title: 'Payment Error',
              message: 'Insufficient stars for payment. Please top up your stars balance via @PremiumBot.',
              buttons: [
                { type: 'default', text: 'Cancel' },
                { type: 'ok', text: 'Buy Stars' }
              ]
            });

            WebApp.MainButton.setText('Buy Stars');
            WebApp.MainButton.onClick(() => {
              WebApp.openTelegramLink('https://t.me/PremiumBot?start=buy_stars');
            });
            WebApp.MainButton.show();
          } else if (status === 'cancelled') {
            TelegramWebApp.HapticFeedback.notificationOccurred('warning');
            TelegramWebApp.showPopup({
              title: 'Payment Cancelled',
              message: 'You cancelled the payment. Try again when you are ready.',
              buttons: [{ type: 'default', text: 'OK' }]
            });
          } else if (status === 'pending') {
            TelegramWebApp.HapticFeedback.notificationOccurred('warning');
            TelegramWebApp.showPopup({
              title: 'Payment Processing',
              message: 'Your payment is being processed. Please wait.',
              buttons: [{ type: 'default', text: 'OK' }]
            });
          }
          setIsProcessing(false);
        });
      } catch (invoiceError) {
        console.error('[Payment] Error opening invoice:', invoiceError);
        TelegramWebApp.HapticFeedback.notificationOccurred('error');
        TelegramWebApp.showPopup({
          title: 'Ошибка',
          message: 'Не удалось открыть форму оплаты. Пожалуйста, попробуйте снова.',
          buttons: [{ type: 'default', text: 'OK' }]
        });
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('[Payment] Stars payment error:', error);
      TelegramWebApp.HapticFeedback.notificationOccurred('error');
      TelegramWebApp.showPopup({
        title: 'Ошибка',
        message: 'Не удалось обработать платеж. Пожалуйста, попробуйте снова.',
        buttons: [{ type: 'default', text: 'OK' }]
      });
      setIsProcessing(false);
      throw error;
    }
  };

  const handleCryptoPayment = async () => {
    try {
      setIsProcessing(true);
      
      console.log('[Payment] Starting crypto payment process');

      const paymentData = {
        amount: PRICES[period],
        period: period,
        currency: 'USD',
        network: 'tron',
        to_currency: 'USDT',
        return_to: `${window.location.origin}/profile`,
        payment_method: 'crypto'
      };

      console.log('[API] Request URL:', '/api/v1/payments/create-crypto');
      console.log('[API] Request method:', 'post');

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/payments/create-crypto`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-telegram-init-data': WebApp.initData
        },
        body: JSON.stringify(paymentData)
      });

      console.log('[API] Response:', { status: response.status, url: '/api/v1/payments/create-crypto', data: await response.clone().json() });

      if (!response.ok) {
        throw new Error('Failed to create payment');
      }

      const data = await response.json();
      
      console.log('[Payment] Payment record created:', data);

      const botData = {
        action: 'crypto_payment',
        data: {
          ...paymentData,
          payment_id: data.id,
          payment_url: data.payment_details.payment_url
        }
      };
      
      console.log('[Payment] Sending payment data to bot:', botData);
      
      WebApp.sendData(JSON.stringify(botData));
      
      console.log('[Telegram.WebView] > postEvent', 'web_app_data_send', { data: JSON.stringify(botData) });

      WebApp.openLink(data.payment_details.payment_url);

    } catch (error) {
      console.error('[Payment] Error:', error);
      WebApp.showPopup({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to process payment',
        buttons: [{ type: 'default', text: 'OK' }]
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setIsProcessing(true);
      TelegramWebApp.HapticFeedback.impactOccurred('heavy');

      if (paymentMethod === 'stars') {
        await handleStarsPayment();
      } else {
        await handleCryptoPayment();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    handleSubscribe
  };
};

